/**
 * The camera hands off between owners all the time — an item scan opens a screen whose zone
 * scanner starts at once, and closing that scanner gives the camera back to the item screen
 * underneath. Whatever the camera was pointed at is usually still in frame, and Scandit re-reads
 * it the instant capture is re-enabled: the pack's barcode would be looked up as a zone, or the
 * rack label just accepted would be fed to the item screen.
 *
 * So, when the camera passes between an item scanner and a zone scanner (either way), a camera read
 * identical to the last scan is treated as an echo and dropped — but only if that last scan was
 * itself recent, i.e. the camera is plausibly still looking at it. It stays an echo for as long as
 * the camera keeps seeing it (each dropped read refreshes the window), and stops being one once the
 * camera has looked away for HANDOFF_ECHO_MS or read anything else.
 *
 * Zone-to-zone hand-offs are left alone: the same rack label is a perfectly good answer to the next
 * zone question (the next lot stocked into the same zone), and where it is not, the zone scanner
 * already refuses it with a reason. Item-to-item hand-offs are left alone too: those screens
 * already have their own rules for a pack still in frame.
 *
 * Camera reads only. A trigger pull is a deliberate act, never an echo.
 */

/** How long a label stays an echo after the camera last read it. */
export const HANDOFF_ECHO_MS = 5000;

/** How soon the camera re-arms after dropping an echo — the zone scanner's own fast re-arm. */
export const HANDOFF_ECHO_REARM_MS = 900;

export type HandoffEcho = { text: string; at: number } | null;

export type LastScan = {
  text: string;
  /** Read while a zone scanner owned the camera. */
  zone: boolean;
  /** When it was read. */
  at: number;
} | null;

/** The echo to watch for once the camera has changed hands, or null when there is none. */
export const handoffEchoFor = (
  last: LastScan,
  zoneOwnerNow: boolean,
  now: number
): HandoffEcho =>
  last && last.zone !== zoneOwnerNow && now - last.at <= HANDOFF_ECHO_MS
    ? { text: last.text, at: now }
    : null;

/** Whether a camera read is the hand-off echo, and the echo to keep watching for afterwards. */
export const screenHandoffEcho = (
  echo: HandoffEcho,
  text: string,
  now: number
): { drop: boolean; echo: HandoffEcho } => {
  if (!echo || text !== echo.text || now - echo.at > HANDOFF_ECHO_MS) {
    return { drop: false, echo: null };
  }
  return { drop: true, echo: { text, at: now } };
};
